import React, { Fragment } from 'react';
import {
  FormElement,
  FormListItem,
  FormListSubHeader,
  TextInput,
} from './CommonFormComponents';
import _ from 'lodash';
import { ATTACH_DOCUMENT_TYPE_COMPANY } from '../utilities/constants';
import AttachDocument from './forms/AttachDocument';
import { Description } from './Description';
import { isWorkerAddOnCompensationStatus } from '../utilities/companySafetyUtil';
import { RadioButtonIcon } from './icons/RadioButtonIcon';
import { DateInput } from './DateInput';
import { isNullOrUndefinedOrEmptyStr } from '../utilities/stringUtil';
import { wrapperValidExpirationDate } from '../utilities/dateSelectorUtility';

export const WorkerAddOnCompensationForm = ({
  readOnly = true,
  checkboxList = {},
  companyDocuments = [],
  documentCodes = [],
  events = {},
  insuranceInfo = {},
  validateMessages = {},
}) => {
  const handleChangeInputValue = event => {
    events.setFormValue('company_branch_insurance', event);
  };

  const handleChangeDate = (name, date) => {
    events.setCalendar('company_branch_insurance', name, date);
  };

  const handleValidate = ({ target: { name } }) => {
    events.validate('company_branch_insurance', name);
  };

  const formatDisplayAmount = () => {
    const amount = _.defaultTo(
      insuranceInfo.worker_add_on_compensation_amount,
      ''
    );
    if (readOnly && _.toString(amount)) {
      return `${new Intl.NumberFormat('jp-JP').format(amount)} 円`;
    }
    return amount;
  };

  const workerAddOnCompensationStatus = isWorkerAddOnCompensationStatus(
    insuranceInfo.worker_add_on_compensation_flag
  );

  const workerAddOnCompensationAttachments = filterWorkerAddOnCompensationFileTypeL(
    companyDocuments
  );

  const showWarningDescription =
    readOnly &&
    workerAddOnCompensationStatus &&
    (_.isEmpty(insuranceInfo.worker_add_on_compensation_start_date) ||
      _.isEmpty(insuranceInfo.worker_add_on_compensation_end_date) ||
      _.isEmpty(_.toString(insuranceInfo.worker_add_on_compensation_amount)) ||
      _.isEmpty(insuranceInfo.worker_add_on_compensation_company_name) ||
      _.isEmpty(insuranceInfo.worker_add_on_compensation_number) ||
      _.isEmpty(workerAddOnCompensationAttachments));

  return (
    <div className='WorkerAddOnCompensationForm'>
      <FormListSubHeader title='労災上乗せ保険情報' />
      {showWarningDescription && (
        <div className='form-list-default__ExceptionalItems'>
          <Description type='primary'>
            <div className='DescriptionText--warning'>
              労災上乗せ保険の登録内容に誤りがあります。正しい情報に更新してください。
            </div>
          </Description>
        </div>
      )}
      <FormListItem
        label='加入状況'
        data-testid='worker_add_on_compensation_flag'
      >
        <FormElement
          validate={validateMessages.worker_add_on_compensation_flag}
        >
          {_.map(
            checkboxList.workmans_compensation_insurance_statuses,
            (answer, index) =>
              readOnly ? (
                insuranceInfo.worker_add_on_compensation_flag ===
                  answer.value && <span key={index}>{answer.name}</span>
              ) : (
                <div className='form-list-radio__item' key={index}>
                  <label className='radio'>
                    <input
                      type='radio'
                      id={`form-radio-worker_add_on_compensation_flag-${
                        answer.id
                      }`}
                      value={answer.value}
                      name='worker_add_on_compensation_flag'
                      checked={
                        insuranceInfo.worker_add_on_compensation_flag ===
                        answer.value
                      }
                      onChange={handleChangeInputValue}
                    />
                    <RadioButtonIcon className='icon--2x' />
                    <span>{answer.name}</span>
                  </label>
                </div>
              )
          )}
        </FormElement>
      </FormListItem>
      {workerAddOnCompensationStatus && (
        <Fragment>
          <FormListItem label='保険開始日'>
            {(insuranceInfo.worker_add_on_compensation_start_date ||
              !readOnly) && (
              <DateInput
                dateString={formatDateForDateInput(
                  insuranceInfo.worker_add_on_compensation_start_date
                )}
                disabled={readOnly}
                onChangeValue={date =>
                  handleChangeDate(
                    'worker_add_on_compensation_start_date',
                    date
                  )
                }
                isPreview={readOnly}
                message={validateMessages.worker_add_on_compensation_start_date}
                onBlur={() =>
                  handleValidate({
                    target: { name: 'worker_add_on_compensation_start_date' },
                  })
                }
                jpDelimiterChar={true}
              />
            )}
          </FormListItem>
          <FormListItem label='保険終了日'>
            {(insuranceInfo.worker_add_on_compensation_end_date ||
              !readOnly) && (
              <DateInput
                dateString={formatDateForDateInput(
                  insuranceInfo.worker_add_on_compensation_end_date
                )}
                disabled={readOnly}
                onChangeValue={date =>
                  handleChangeDate('worker_add_on_compensation_end_date', date)
                }
                isPreview={readOnly}
                message={validateMessages.worker_add_on_compensation_end_date}
                warning={wrapperValidExpirationDate(
                  formatDateForDateInput(
                    insuranceInfo.worker_add_on_compensation_end_date
                  ),
                  null,
                  true,
                  validateMessages.worker_add_on_compensation_end_date
                )}
                onBlur={() =>
                  handleValidate({
                    target: { name: 'worker_add_on_compensation_end_date' },
                  })
                }
                jpDelimiterChar={true}
              />
            )}
          </FormListItem>
          <FormListItem label='保険金額'>
            <FormElement
              validate={validateMessages.worker_add_on_compensation_amount}
            >
              <TextInput
                type='text'
                name='worker_add_on_compensation_amount'
                className={!readOnly && 'text-right'}
                maxLength={12}
                disabled={readOnly}
                value={formatDisplayAmount()}
                onChange={handleChangeInputValue}
                onBlur={handleValidate}
              />
              {!readOnly && <span className='text-currency'>円</span>}
            </FormElement>
          </FormListItem>
          <FormListItem label='保険会社名'>
            <FormElement
              validate={
                validateMessages.worker_add_on_compensation_company_name
              }
            >
              <TextInput
                type='text'
                name='worker_add_on_compensation_company_name'
                maxLength={100}
                disabled={readOnly}
                value={_.defaultTo(
                  insuranceInfo.worker_add_on_compensation_company_name,
                  ''
                )}
                onChange={handleChangeInputValue}
                onBlur={handleValidate}
              />
            </FormElement>
          </FormListItem>
          <FormListItem label='保険証券番号'>
            <FormElement
              validate={validateMessages.worker_add_on_compensation_number}
            >
              <TextInput
                type='text'
                name='worker_add_on_compensation_number'
                maxLength={20}
                disabled={readOnly}
                value={_.defaultTo(
                  insuranceInfo.worker_add_on_compensation_number,
                  ''
                )}
                onChange={handleChangeInputValue}
                onBlur={handleValidate}
              />
            </FormElement>
          </FormListItem>
          <FormListItem label='加入証明書'>
            <FormElement>
              <AttachDocument
                companyDocuments={workerAddOnCompensationAttachments}
                documentCodes={filterWorkerAddOnCompensationFileTypeL(
                  documentCodes
                )}
                events={events}
                readOnly={readOnly}
                isFormListDefault={true}
                indexId={
                  ATTACH_DOCUMENT_TYPE_COMPANY.WORKER_ADD_ON_COMPENSATION
                }
              />
            </FormElement>
          </FormListItem>
        </Fragment>
      )}
    </div>
  );
};

export const filterWorkerAddOnCompensationFileTypeL = listInput => {
  return _.filter(listInput, {
    file_typeL: ATTACH_DOCUMENT_TYPE_COMPANY.WORKER_ADD_ON_COMPENSATION,
  });
};

export const removeWorkerAddOnCompensationFileTypeL = listInput => {
  return _.filter(
    listInput,
    file =>
      file.file_typeL !==
      ATTACH_DOCUMENT_TYPE_COMPANY.WORKER_ADD_ON_COMPENSATION
  );
};

const formatDateForDateInput = date => {
  if (isNullOrUndefinedOrEmptyStr(date)) {
    return '//';
  }
  return date;
};



// WEBPACK FOOTER //
// ./src/components/WorkerAddOnCompensationForm.jsx